import { useCallback, useEffect, useState } from 'react';
import { Plus, X, Shield } from 'lucide-react';
import { useAuthedApi } from '../lib/useAuthedApi';

type Person = { staff_id: string; name: string; email: string };
type ManagersResponse = { managers: Person[]; candidates: Person[] };

/**
 * Manage a location's managers. Backed by /locations/:id/managers, which the
 * backend authorizes for org admins and existing managers of the location — so
 * this same component works on the admin Locations page and on a manager's
 * dashboard (a manager can promote peers at a location they run).
 */
export default function LocationManagers({ locationId }: { locationId: string }) {
  const apiFn = useAuthedApi();
  const [data, setData] = useState<ManagersResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [addId, setAddId] = useState('');

  const load = useCallback(() => {
    apiFn<ManagersResponse>(`/locations/${locationId}/managers`)
      .then(setData)
      .catch((e) => setErr((e as Error).message));
  }, [apiFn, locationId]);

  useEffect(() => {
    load();
  }, [load]);

  async function add() {
    if (!addId) return;
    setBusy(true);
    setErr(null);
    try {
      await apiFn(`/locations/${locationId}/managers`, {
        method: 'POST',
        body: { staff_id: addId },
      });
      setAddId('');
      load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(staffId: string) {
    setBusy(true);
    setErr(null);
    try {
      await apiFn(`/locations/${locationId}/managers/${staffId}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!data) return <p className="muted small">Loading managers…</p>;

  return (
    <div>
      {data.managers.length === 0 ? (
        <p className="muted small" style={{ margin: '0 0 0.5rem' }}>
          No managers yet — a manager sees and manages every appointment at this location.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.5rem', display: 'grid', gap: '0.35rem' }}>
          {data.managers.map((m) => (
            <li key={m.staff_id} className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
              <Shield size={14} />
              <span>
                {m.name} <span className="muted small">· {m.email}</span>
              </span>
              <span className="spacer" />
              <button
                className="icon-btn icon-btn-danger"
                onClick={() => remove(m.staff_id)}
                disabled={busy}
                aria-label={`Remove ${m.name} as manager`}
                title="Remove manager"
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {data.candidates.length > 0 && (
        <div className="row" style={{ gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={addId} onChange={(e) => setAddId(e.target.value)} disabled={busy}>
            <option value="">Add a manager…</option>
            {data.candidates.map((c) => (
              <option key={c.staff_id} value={c.staff_id}>
                {c.name} ({c.email})
              </option>
            ))}
          </select>
          <button onClick={add} disabled={busy || !addId}>
            <Plus size={14} /> Add
          </button>
        </div>
      )}

      {err && <p className="err small" style={{ marginBottom: 0 }}>{err}</p>}
    </div>
  );
}
